'use client';

import React, { useEffect, useMemo, useState } from 'react';
import ArtigoToc, { HEADER_OFFSET, slugify, type TocItem } from '../../../components/ArtigoToc';
import ArticleContentStyles from '../../../components/ArticleContentStyles';
import Logo from '../../../components/Logo';
import ServicoCta from '../../../components/ServicoCta';
import type { Bloco, Metrica, CaseTextos } from '../../../lib/cases-api';

export type { Bloco, Metrica, CaseTextos };

/**
 * Renderiza um case usando exatamente o mesmo layout de leitura da pagina de
 * artigo (`/artigos/[slug]`): coluna de 760px + sumario lateral sticky com
 * scrollspy, e a folha `.article-content` compartilhada. A diferenca e a
 * origem do conteudo - aqui os blocos vem tipados do messages/*.json e sao
 * montados em JSX, entao nao ha dangerouslySetInnerHTML nem necessidade de
 * injetar ids no DOM depois de montar: os ids das secoes ja sao gerados aqui.
 */
export default function CaseConteudo({
  textos,
  clienteNome,
  clienteLogoUrl,
  clienteSiteUrl,
  nesteCase,
  sumarioAria,
  blocos,
  blocos2,
  metricas,
  sobreNumeros,
  sobreParagrafos,
  sobreEnqParagrafos,
}: {
  textos: CaseTextos;
  clienteNome: string;
  clienteLogoUrl: string | null;
  clienteSiteUrl: string | null;
  nesteCase: string;
  sumarioAria: string;
  blocos: Bloco[];
  blocos2: Bloco[];
  metricas: Metrica[];
  sobreNumeros: Metrica[];
  sobreParagrafos: string[];
  sobreEnqParagrafos: string[];
}) {
  // Ids das secoes: gerados uma vez a partir dos titulos, com sufixo numerico
  // para o caso de dois titulos com o mesmo texto (mesma regra do artigo).
  const { itens, idsPorBloco } = useMemo(() => {
    const usados = new Map<string, number>();
    const lista: TocItem[] = [];
    const mapa = new Map<string, string>();

    function registrar(chave: string, texto: string) {
      const base = slugify(texto) || 'secao';
      const n = usados.get(base) || 0;
      usados.set(base, n + 1);
      const id = n === 0 ? base : `${base}-${n}`;
      mapa.set(chave, id);
      lista.push({ id, texto, nivel: 2 });
      return id;
    }

    blocos.forEach((b, i) => {
      if (b.tipo === 'h2') registrar(`a-${i}`, b.texto);
    });
    registrar('metricas', textos.metricasTitulo);
    blocos2.forEach((b, i) => {
      if (b.tipo === 'h2') registrar(`b-${i}`, b.texto);
    });
    registrar('sobre-enq', textos.sobreEnqTitulo);
    registrar('sobre-cliente', textos.sobreTitulo);

    return { itens: lista, idsPorBloco: mapa };
  }, [blocos, blocos2, textos.metricasTitulo, textos.sobreEnqTitulo, textos.sobreTitulo]);

  const [ativoId, setAtivoId] = useState<string | null>(null);

  // Scrollspy: mesma regra do artigo (linha de leitura a 33% da viewport).
  useEffect(() => {
    if (itens.length < 2) return;

    if (window.location.hash) {
      const alvo = document.getElementById(decodeURIComponent(window.location.hash.slice(1)));
      if (alvo) {
        window.scrollTo({ top: alvo.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET });
      }
    }

    let agendado = false;
    function aoRolar() {
      if (agendado) return;
      agendado = true;
      window.requestAnimationFrame(() => {
        agendado = false;
        const linha = window.innerHeight * 0.33;
        let atual = 0;
        for (let i = 0; i < itens.length; i++) {
          const el = document.getElementById(itens[i].id);
          if (el && el.getBoundingClientRect().top <= linha) {
            atual = i;
          } else if (el) {
            break;
          }
        }
        setAtivoId(itens[atual]?.id ?? null);
      });
    }

    window.addEventListener('scroll', aoRolar, { passive: true });
    window.addEventListener('resize', aoRolar, { passive: true });
    aoRolar();
    return () => {
      window.removeEventListener('scroll', aoRolar);
      window.removeEventListener('resize', aoRolar);
    };
  }, [itens]);

  function renderBlocos(lista: Bloco[], prefixo: string) {
    return lista.map((b, i) => {
      if (b.tipo === 'h2' || b.tipo === 'h3') {
        const id = idsPorBloco.get(`${prefixo}-${i}`);
        if (b.tipo === 'h3') {
          return <h3 key={i}>{b.texto}</h3>;
        }
        return (
          <h2 key={i} id={id}>
            {b.texto}
          </h2>
        );
      }
      if (b.tipo === 'lista') {
        return (
          <ul key={i}>
            {b.itens.map((item, j) => (
              <li key={`${i}-${j}`}>{item}</li>
            ))}
          </ul>
        );
      }
      return <p key={i}>{b.texto}</p>;
    });
  }

  const temToc = itens.length >= 2;

  return (
    <div
      className={`grid grid-cols-1 ${temToc ? 'artigo-grid-com-toc' : ''}`}
      style={{ gap: '48px' }}
    >
      <style>{`
        @media (min-width: 1024px) {
          .artigo-grid-com-toc { grid-template-columns: minmax(0, 1fr) 320px; }
        }
        @media (min-width: 1024px) { .artigo-toc-mobile { display: none !important; } }
      `}</style>

      <article style={{ maxWidth: '760px' }}>
        <p className="eyebrow mb-3" style={{ color: '#1a9fff' }}>
          {textos.eyebrow}
        </p>

        {/* Logotipo do cliente logo no inicio do case: ancora de marca antes do titulo. */}
        {clienteLogoUrl && (
          <img
            src={clienteLogoUrl}
            alt={clienteNome}
            style={{ height: '40px', width: 'auto', marginBottom: '20px' }}
          />
        )}

        <h1 className="display-section mb-4" style={{ fontSize: 'clamp(28px, 4vw, 44px)' }}>
          {textos.caseTitulo}
        </h1>

        <div className="flex items-center flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-3 text-gray-500" style={{ fontSize: '13px' }}>
            <span>{textos.caseData}</span>
            <span>&middot;</span>
            <span>{textos.caseSegmento}</span>
          </div>
        </div>

        {temToc && (
          <div className="mb-8">
            <div className="artigo-toc-mobile">
              <ArtigoToc
                itens={itens}
                ativoId={ativoId}
                titulo={nesteCase}
                ariaLabel={sumarioAria}
              />
            </div>
          </div>
        )}

        <ArticleContentStyles />
        <div className="article-content text-gray-800">
          {/* Linha fina do release, em destaque acima do corpo. */}
          <p>
            <strong>{textos.caseResumo}</strong>
          </p>

          {renderBlocos(blocos, 'a')}

          <blockquote
            style={{
              borderLeft: '4px solid #1a9fff',
              background: '#eef6fd',
              padding: '20px 22px',
              margin: '32px 0',
              borderRadius: '0 6px 6px 0',
            }}
          >
            <p style={{ fontStyle: 'italic', marginBottom: '16px' }}>
              &ldquo;{textos.caseQuoteTexto}&rdquo;
            </p>
            <footer className="flex items-center" style={{ gap: '12px' }}>
              <Logo style={{ height: '26px', width: 'auto', flexShrink: 0 }} />
              <div>
                <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f2740' }}>
                  {textos.caseQuoteAutor}
                </p>
                <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                  {textos.caseQuoteCargo}
                </p>
              </div>
            </footer>
          </blockquote>

          <h2 id={idsPorBloco.get('metricas')}>{textos.metricasTitulo}</h2>
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
              gap: '18px',
              margin: '0 0 28px',
              padding: '22px',
              background: '#f5f7fa',
              border: '1px solid #e8ecf1',
              borderRadius: '8px',
            }}
          >
            {metricas.map((m, i) => (
              <div key={`m-${i}`}>
                <p
                  style={{
                    margin: 0,
                    fontSize: '26px',
                    fontWeight: 700,
                    lineHeight: 1.05,
                    color: '#0f2740',
                  }}
                >
                  {m.valor}
                </p>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: '12px',
                    lineHeight: 1.35,
                    color: '#6b7280',
                  }}
                >
                  {m.label}
                </p>
              </div>
            ))}
          </div>

          {renderBlocos(blocos2, 'b')}

          <h2 id={idsPorBloco.get('sobre-enq')}>{textos.sobreEnqTitulo}</h2>
          <Logo style={{ height: '30px', width: 'auto', margin: '0 0 16px' }} />
          {sobreEnqParagrafos.map((p, i) => (
            <p key={`se-${i}`}>{p}</p>
          ))}

          <h2 id={idsPorBloco.get('sobre-cliente')}>{textos.sobreTitulo}</h2>
          {clienteLogoUrl && (
            <img
              src={clienteLogoUrl}
              alt={clienteNome}
              style={{ height: '30px', width: 'auto', margin: '0 0 16px' }}
            />
          )}
          {sobreParagrafos.map((p, i) => (
            <p key={`sc-${i}`}>{p}</p>
          ))}

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(110px, 1fr))',
              gap: '18px',
              margin: '24px 0',
              paddingTop: '20px',
              borderTop: '1px solid #e5e5e5',
            }}
          >
            {sobreNumeros.map((n, i) => (
              <div key={`n-${i}`}>
                <p
                  style={{
                    margin: 0,
                    fontSize: '22px',
                    fontWeight: 700,
                    lineHeight: 1.05,
                    color: '#0f2740',
                  }}
                >
                  {n.valor}
                </p>
                <p
                  style={{
                    margin: '4px 0 0',
                    fontSize: '12px',
                    lineHeight: 1.35,
                    color: '#6b7280',
                  }}
                >
                  {n.label}
                </p>
              </div>
            ))}
          </div>

          <p>{textos.sobreFechamento}</p>

          {clienteSiteUrl && (
            <p style={{ marginBottom: '8px' }}>
              <a href={clienteSiteUrl} target="_blank" rel="noopener noreferrer">
                {textos.caseLinkSite}
              </a>
            </p>
          )}
          <p style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: '#0f2740' }}>
            {textos.sobreAssinaturaNome}
          </p>
          <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
            {textos.sobreAssinaturaCargo}
          </p>
        </div>

        {/* CTA ao final, mesmo lugar do botao de e-book no artigo. */}
        <div className="mt-12" style={{ borderTop: '1px solid #e5e5e5', paddingTop: '40px' }}>
          <p className="eyebrow mb-2" style={{ color: '#1a9fff' }}>
            {textos.ctaEyebrow}
          </p>
          <h2 className="display-section mb-3" style={{ fontSize: 'clamp(22px, 2.8vw, 30px)' }}>
            {textos.ctaTitulo}
          </h2>
          <p className="text-gray-600 mb-6" style={{ fontSize: '15px', lineHeight: 1.65 }}>
            {textos.ctaDescricao}
          </p>
          <ServicoCta href="chat" label={textos.ctaLabel} className="btn-primary inline-flex" />
        </div>
      </article>

      {temToc && (
        <aside
          className="hidden lg:block"
          style={{ position: 'sticky', top: '130px', alignSelf: 'start' }}
        >
          <ArtigoToc itens={itens} ativoId={ativoId} titulo={nesteCase} ariaLabel={sumarioAria} />
        </aside>
      )}
    </div>
  );
}
